import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector, useDispatch } from 'react-redux';
import CustomAxios from '../../utils/CustomAxios';
import { updatepageName } from '../../Redux/page';
import {
  GrowthModal,
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  GrowthPageLoader,
  GrowthFormGrid,
  GrowthFormSwitch,
} from '../growth_hub/GrowthModal';

const emptyForm = {
  inventory: '',
  allow_partial_wallet: true,
  partial_wallet_percentage: '10',
  allow_full_wallet: true,
  /*
   * Bonus credit, defaulted ON and UNCAPPED.
   *
   * The opposite of how the two flags above behave, and deliberately: a hub
   * with no row has the paid wallet off, because it was never enabled. Bonus
   * spending already works everywhere, so a new config row must not silently
   * take it away — the admin narrows it on purpose.
   */
  allow_bonus_wallet: true,
  bonus_wallet_percentage: '100',
  max_bonus_amount: '',
};

/**
 * Hub-wise wallet checkout options shown on mobile Payment Method.
 * Partial % and/or full wallet pay — opt-in per hub (no row = both off on mobile).
 */
/**
 * What the bonus column says, in one glance.
 *
 * Three genuinely different states and they must not collapse into each other:
 * off entirely, capped, and no limit. A blank cell for "no limit" would read as
 * "not configured", which is the one thing it is not.
 */
const bonusLabel = (row) => {
    if (row.allow_bonus_wallet === false) return 'Off';
    const pct = Number(row.bonus_wallet_percentage ?? 100);
    const cap = row.max_bonus_amount;
    const parts = [];
    if (Number.isFinite(pct) && pct < 100) parts.push(`${pct}%`);
    if (cap !== null && cap !== undefined && cap !== '') parts.push(`max ₹${cap}`);
    return parts.length ? parts.join(' · ') : 'No limit';
};

export function WalletCheckoutConfig() {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const inventoryList = useSelector((state) => state.inventory.inventory) || [];

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    dispatch(updatepageName('wallet_checkout_config'));
    fetchConfigs();
  }, [dispatch]);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3500 });
  };

  const hubLabel = (inventoryId) => {
    const hub = inventoryList.find((h) => Number(h.value ?? h.id) === Number(inventoryId));
    if (!hub) return `Hub #${inventoryId}`;
    return hub.label || hub.place || hub.name || `Hub #${inventoryId}`;
  };

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await CustomAxios.get('wallet-checkout-config/');
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setConfigs(data);
    } catch (err) {
      showToast(
        'error',
        'Error',
        err?.response?.data?.detail || 'Failed to load wallet checkout configs'
      );
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditMode(false);
    setSelectedId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditMode(true);
    setSelectedId(row.id);
    setForm({
      inventory: row.inventory,
      allow_partial_wallet: Boolean(row.allow_partial_wallet),
      partial_wallet_percentage: String(row.partial_wallet_percentage ?? '10'),
      allow_full_wallet: Boolean(row.allow_full_wallet),
      allow_bonus_wallet: row.allow_bonus_wallet !== false,
      bonus_wallet_percentage: String(row.bonus_wallet_percentage ?? '100'),
      // '' not '0' — an empty box means "no flat cap", and 0 means "no bonus".
      max_bonus_amount:
        row.max_bonus_amount === null || row.max_bonus_amount === undefined
          ? ''
          : String(row.max_bonus_amount),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.inventory) {
      showToast('warn', 'Validation', 'Select a hub');
      return;
    }
    if (form.allow_partial_wallet) {
      const pct = Number(form.partial_wallet_percentage);
      if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
        showToast('warn', 'Validation', 'Partial % must be between 0 and 100');
        return;
      }
    }
    if (!form.allow_partial_wallet && !form.allow_full_wallet) {
      // Allowed (3B): mobile shows balance + Top UP only
    }
    if (form.allow_bonus_wallet) {
      const bp = Number(form.bonus_wallet_percentage);
      if (!Number.isFinite(bp) || bp < 0 || bp > 100) {
        showToast('warn', 'Validation', 'Bonus % must be between 0 and 100');
        return;
      }
      if (form.max_bonus_amount !== '') {
        const cap = Number(form.max_bonus_amount);
        if (!Number.isFinite(cap) || cap < 0) {
          showToast('warn', 'Validation', 'Bonus cap must be 0 or more');
          return;
        }
      }
    }

    const payload = {
      inventory: Number(form.inventory),
      allow_partial_wallet: Boolean(form.allow_partial_wallet),
      partial_wallet_percentage: form.partial_wallet_percentage || '10',
      allow_full_wallet: Boolean(form.allow_full_wallet),
      allow_bonus_wallet: Boolean(form.allow_bonus_wallet),
      bonus_wallet_percentage: form.bonus_wallet_percentage || '100',
      // null, not 0. An empty box is "no flat cap"; 0 would mean no bonus at all.
      max_bonus_amount:
        form.max_bonus_amount === '' ? null : form.max_bonus_amount,
    };

    try {
      setSaving(true);
      if (editMode && selectedId) {
        await CustomAxios.patch(`wallet-checkout-config/${selectedId}/`, payload);
        showToast('success', 'Saved', 'Wallet checkout config updated');
      } else {
        await CustomAxios.post('wallet-checkout-config/', payload);
        showToast('success', 'Created', 'Wallet checkout config created');
      }
      setModalOpen(false);
      fetchConfigs();
    } catch (err) {
      const detail =
        err?.response?.data?.inventory?.[0] ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.response?.data?.detail ||
        err?.response?.data?.partial_wallet_percentage?.[0] ||
        JSON.stringify(err?.response?.data || {}) ||
        'Save failed';
      showToast('error', 'Error', String(detail));
    } finally {
      setSaving(false);
    }
  };

  const configuredHubIds = new Set(configs.map((c) => Number(c.inventory)));

  return (
    <Box sx={{ p: 2 }}>
      <Toast ref={toast} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Wallet Checkout (per hub)</Typography>
          <Typography variant="body2" color="text.secondary">
            Controls what the mobile Payment Method shows: partial % wallet, full wallet, or both.
            Hubs without a row default to both enabled at 10%.
          </Typography>
        </Box>
        <GrowthPrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
          Add hub config
        </GrowthPrimaryButton>
      </Box>

      {loading ? (
        <GrowthPageLoader label="Loading wallet checkout configs…" />
      ) : (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Hub</TableCell>
              <TableCell>Partial %</TableCell>
              <TableCell>Allow partial</TableCell>
              <TableCell>Allow full pay</TableCell>
              <TableCell>Bonus limit</TableCell>
              <TableCell align="right">Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  No configs yet — mobile hubs use default (both on @ 10%) until you add one.
                </TableCell>
              </TableRow>
            )}
            {configs.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.inventory_place || hubLabel(row.inventory)}</TableCell>
                <TableCell>{row.partial_wallet_percentage}%</TableCell>
                <TableCell>{row.allow_partial_wallet ? 'Yes' : 'No'}</TableCell>
                <TableCell>{row.allow_full_wallet ? 'Yes' : 'No'}</TableCell>
                <TableCell>{bonusLabel(row)}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openEdit(row)} size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <GrowthModal
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editMode ? 'Edit wallet checkout' : 'Add wallet checkout'}
        subtitle="Partial % and full-wallet options for mobile checkout"
        maxWidth={{ xs: '94%', sm: 440 }}
        busy={saving}
        busyLabel={editMode ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={handleSave} loading={saving}>
              Save
            </GrowthPrimaryButton>
          </>
        }
      >
          <GrowthFormGrid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" disabled={editMode}>
                <InputLabel>Hub</InputLabel>
                <Select
                  label="Hub"
                  value={form.inventory}
                  onChange={(e) => setForm({ ...form, inventory: e.target.value })}
                >
                  {inventoryList
                    .filter(
                      (h) =>
                        editMode ||
                        !configuredHubIds.has(Number(h.value ?? h.id))
                    )
                    .map((h) => (
                      <MenuItem key={h.value ?? h.id} value={h.value ?? h.id}>
                        {h.label || h.place || h.name || `Hub #${h.value ?? h.id}`}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <GrowthFormSwitch
                label="Allow partial wallet (% of order)"
                checked={form.allow_partial_wallet}
                onChange={(e) =>
                  setForm({ ...form, allow_partial_wallet: e.target.checked })
                }
              />
            </Grid>
            {form.allow_partial_wallet && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Partial wallet %"
                  value={form.partial_wallet_percentage}
                  onChange={(e) =>
                    setForm({ ...form, partial_wallet_percentage: e.target.value })
                  }
                  inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                  helperText="e.g. 10 = user can apply 10% of the order from wallet"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <GrowthFormSwitch
                label="Allow full amount via wallet"
                checked={form.allow_full_wallet}
                onChange={(e) =>
                  setForm({ ...form, allow_full_wallet: e.target.checked })
                }
              />
            </Grid>
            {!form.allow_partial_wallet && !form.allow_full_wallet && (
              <Grid item xs={12}>
                <Typography variant="caption" color="warning.main">
                  Both off: mobile still shows wallet balance + Top UP, but no apply/full-pay options.
                </Typography>
              </Grid>
            )}

            {/*
              Bonus credit — its own block, below a divider.

              Separated from the two switches above because it is not a third
              way of paying. Bonus rides alongside whichever of those is chosen
              and comes off whatever is still owed, so grouping it with them
              would read as a mutually exclusive alternative.
            */}
            <Grid item xs={12}>
              <Box sx={{ borderTop: '1px solid #eee', pt: 2, mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Bonus credit
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Promotional credit granted by admin. Applied after wallet and
                  loyalty, on the remaining amount.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <GrowthFormSwitch
                label="Allow bonus credit at checkout"
                checked={form.allow_bonus_wallet}
                onChange={(e) =>
                  setForm({ ...form, allow_bonus_wallet: e.target.checked })
                }
              />
            </Grid>

            {form.allow_bonus_wallet && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Max % of order"
                    value={form.bonus_wallet_percentage}
                    onChange={(e) =>
                      setForm({ ...form, bonus_wallet_percentage: e.target.value })
                    }
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    helperText="100 = no limit. 20 = bonus covers at most 20% of an order."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Max ₹ per order (optional)"
                    value={form.max_bonus_amount}
                    onChange={(e) =>
                      setForm({ ...form, max_bonus_amount: e.target.value })
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Leave blank for no flat cap. The lower of the two applies."
                  />
                </Grid>
                {Number(form.bonus_wallet_percentage) === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="warning.main">
                      0% means bonus credit cannot be spent here at all — the same
                      as switching it off.
                    </Typography>
                  </Grid>
                )}
              </>
            )}

            {!form.allow_bonus_wallet && (
              <Grid item xs={12}>
                <Typography variant="caption" color="warning.main">
                  Customers holding bonus credit will not be able to spend it on
                  orders from this hub.
                </Typography>
              </Grid>
            )}
          </GrowthFormGrid>
      </GrowthModal>
    </Box>
  );
}

export default WalletCheckoutConfig;
